import {SlackIcon} from '@github-ui/custom-icons/SlackIcon'
import {matchesFocusVisible, useFocusVisibleWithin} from '@github-ui/focus-visible'
import {getArtifactLinkDisplayName} from '@github-ui/scribe-artifacts/artifact-url'
import {ArtifactLink} from '@github-ui/scribe-artifacts/ArtifactLink'
import type {PanelArtifact} from '@github-ui/scribe-artifacts/ArtifactPanelContent'
import {ChecklistIcon, FileIcon, KebabHorizontalIcon, LinkIcon, PencilIcon, TrashIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, registerPortalRoot} from '@primer/react'
import {clsx} from 'clsx'
import {memo, type RefObject, useCallback, useEffect, useRef, useState} from 'react'

import styles from './ArtifactRow.module.css'
import type {EditableArtifact} from './create-artifact-dialog-types'
import type {IssueArtifact} from './types'

function ArtifactTypeIcon({type, body}: {type: IssueArtifact['type']; body: string}) {
  const isSlackLink = type === 'link' && isSlackUrl(body)
  const Icon = type === 'plan' ? ChecklistIcon : type === 'link' ? (isSlackLink ? SlackIcon : LinkIcon) : FileIcon
  return (
    <span className={styles.IconWrap} data-testid={isSlackLink ? 'slack-icon' : undefined}>
      {isSlackLink ? <SlackIcon /> : <Icon size={14} className={styles.Icon} />}
    </span>
  )
}

function isSlackUrl(value: string): boolean {
  try {
    const url = new URL(value, 'https://github.com')
    return (
      (url.protocol === 'https:' || url.protocol === 'http:') &&
      (url.hostname === 'slack.com' || url.hostname.endsWith('.slack.com'))
    )
  } catch {
    return false
  }
}

type PersistedArtifactRowProps = {
  id: number
  number: number
  type: IssueArtifact['type']
  name: string
  body: string
  bodyHTML?: string
  createdAt?: string
  updatedAt?: string
  hasEdits?: boolean
  updatedByActor?: IssueArtifact['updatedByActor']
  resourceId: number
  onDelete?: (id: number) => void
  onEdit?: (artifact: EditableArtifact, returnFocusRef: RefObject<HTMLButtonElement | null>) => void
  onSelect?: (artifact: PanelArtifact) => void
}

export const PersistedArtifactRow = memo(function PersistedArtifactRowImpl({
  id,
  number,
  type,
  name,
  body,
  bodyHTML,
  createdAt,
  updatedAt,
  hasEdits,
  updatedByActor,
  resourceId,
  onDelete,
  onEdit,
  onSelect,
}: PersistedArtifactRowProps) {
  const actionsButtonRef = useRef<HTMLButtonElement>(null)
  const rowRef = useRef<HTMLLIElement>(null)
  const isLink = type === 'link'
  const displayName = isLink ? getArtifactLinkDisplayName(name, body) : name

  // `menuOpen` and `titleFocusVisible` replace `:has()` selectors on `.Row` (menu-open,
  // title focus-visible) to avoid WebKit `:has()` style invalidation across the artifact list.
  // The actions reveal uses the shared `useFocusVisibleWithin` helper, which toggles
  // `data-focus-visible-within` on the actions container via delegated listeners (no re-render).
  const [menuOpen, setMenuOpen] = useState(false)
  const [titleFocusVisible, setTitleFocusVisible] = useState(false)
  const actionsFocusVisibleRef = useFocusVisibleWithin()

  // Use a body-level portal to keep the menu outside the embedded panel's stacking context.
  useEffect(() => {
    registerPortalRoot(document.body, 'issue-artifact-action-menu')
  }, [])

  const handleTitleFocus = useCallback((event: React.FocusEvent<HTMLElement>) => {
    setTitleFocusVisible(matchesFocusVisible(event.currentTarget))
  }, [])
  const handleTitleBlur = useCallback(() => setTitleFocusVisible(false), [])
  const handleClick = useCallback(() => {
    onSelect?.({
      id: `${type}:${id}`,
      number,
      type,
      name,
      body,
      bodyHTML: bodyHTML ?? '',
      createdAt,
      updatedAt,
      hasEdits,
      updatedByActor,
      resourceType: 'issue',
      resourceId,
    })
  }, [id, number, type, name, body, bodyHTML, createdAt, updatedAt, hasEdits, updatedByActor, resourceId, onSelect])

  const handleDelete = useCallback(() => {
    onDelete?.(id)
  }, [id, onDelete])

  const handleEdit = useCallback(() => {
    onEdit?.({number, type, name, body}, actionsButtonRef)
  }, [body, name, number, onEdit, type])

  const setActionMenuOverlayRef = useCallback((node: HTMLDivElement | null) => {
    // Primer's portal wrapper controls the menu's stacking level.
    if (node?.parentElement) node.parentElement.style.zIndex = 'var(--zIndex-overlay)'
  }, [])

  return (
    <li
      ref={rowRef}
      className={clsx(styles.Row, menuOpen && styles.RowMenuOpen, titleFocusVisible && styles.RowTitleFocusVisible)}
    >
      <ArtifactTypeIcon type={type} body={body} />
      <div className={styles.Content}>
        {isLink ? (
          <ArtifactLink
            name={name}
            url={body}
            updatedAt={updatedAt}
            updatedByActor={updatedByActor}
            className={styles.TitleButton}
            textClassName={styles.Title}
            popoverAnchorRef={rowRef}
            onFocus={handleTitleFocus}
            onBlur={handleTitleBlur}
            renderFallback={fallbackDisplayName => (
              <button
                type="button"
                className={styles.TitleButton}
                aria-label={fallbackDisplayName}
                onClick={handleClick}
                onFocus={handleTitleFocus}
                onBlur={handleTitleBlur}
              >
                <span className={styles.Title}>{fallbackDisplayName}</span>
              </button>
            )}
          />
        ) : (
          <button
            type="button"
            className={styles.TitleButton}
            aria-label={displayName}
            onClick={handleClick}
            onFocus={handleTitleFocus}
            onBlur={handleTitleBlur}
          >
            <span className={styles.Title}>{displayName}</span>
          </button>
        )}
      </div>
      <div className={styles.HoverActions} ref={actionsFocusVisibleRef}>
        <ActionMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <ActionMenu.Anchor>
            <IconButton
              ref={actionsButtonRef}
              icon={KebabHorizontalIcon}
              variant="invisible"
              size="small"
              aria-label={`More actions for ${displayName}`}
            />
          </ActionMenu.Anchor>
          <ActionMenu.Overlay ref={setActionMenuOverlayRef} portalContainerName="issue-artifact-action-menu">
            <ActionList>
              <ActionList.Item onSelect={handleEdit}>
                <ActionList.LeadingVisual>
                  <PencilIcon />
                </ActionList.LeadingVisual>
                Edit
              </ActionList.Item>
              <ActionList.Item variant="danger" onSelect={handleDelete}>
                <ActionList.LeadingVisual>
                  <TrashIcon />
                </ActionList.LeadingVisual>
                Delete
              </ActionList.Item>
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      </div>
    </li>
  )
})
